import { useAuth } from "@/utils/auth";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Super user view: the one switch for what renders inside a page.
 *
 * - Roles decide what data someone may read or change (RLS and route guards).
 * - Tabs decide which pages appear in the nav.
 * - The super user view decides how much of a page someone sees: AI tools, advanced
 *   template steps, extra filters and IDs. Components should read `isSuperUserView`
 *   for this, never `roles.isAdmin`. Admin-only actions belong on admin pages.
 *
 * Only super users (users.is_super_user) get the switch (bottom-left), so they can see the
 * app the way most users do. The switch is a display choice saved in this browser only and
 * never written back to the database: it changes what you see, never what you may do.
 */
type SuperUserViewStore = {
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
};

const useSuperUserViewStore = create<SuperUserViewStore>()(
    persist(
        (set) => ({
            enabled: true,
            setEnabled: (enabled) => set({ enabled }),
        }),
        {
            name: "super-user-view",
            storage: createJSONStorage(() => localStorage),
        }
    )
);

export function getSuperUserView({ isSuperUser, enabled }: { isSuperUser: boolean; enabled: boolean }) {
    return { canUseSuperUserView: isSuperUser, isSuperUserView: isSuperUser && enabled };
}

export function useSuperUserView() {
    const auth = useAuth();
    const enabled = useSuperUserViewStore((state) => state.enabled);
    const setEnabled = useSuperUserViewStore((state) => state.setEnabled);

    return {
        ...getSuperUserView({
            isSuperUser: Boolean(auth?.isSuperUser),
            enabled,
        }),
        setSuperUserView: setEnabled,
    };
}
